import React, { Suspense, lazy, useEffect, useState } from "react";
import clsx from "clsx";
import makeStyles from "@material-ui/styles/makeStyles";

const ReactAnimation01 = lazy(() =>
	import(
		/* webpackChunkName: "reactAnimation01" */ "@micado-digital/react-animation/ReactAnimation01"
	)
);

const useStyles = makeStyles(theme => ({
	"@keyframes scale": {
		from: {
			transform: "scale(1)"
		},
		to: {
			transform: "scale(1.1)"
		}
	},
	root: {
		height: "70vh",
		maxHeight: 800,
		position: "relative",
		"& .mco-animation-01__texts": {
			display: "none"
		}
	},
	rootFullscreen: {
		height: "100vh",
		maxHeight: "none",
		"& img": {
			animation: "$scale 20s ease infinite alternate"
		},
		"& .mco-animation-01__texts": {
			alignItems: "center",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
			padding: theme.spacing(3, 2),
			textAlign: "center",
			[theme.breakpoints.up("lg")]: {
				transform: "translate(0, -50px)"
			},
			"& .mco-animation-01__text--1": {
				...theme?.typography?.h1,
				color: theme.palette.text.secondary,
				opacity: 0,
				transform: "translate(0, -10px)",
				textShadow: "0px 4px 20px rgba(0, 0, 0, 0.5)",
				transition: "opacity 2500ms ease 1s, transform 2000ms ease 1s",
				[theme.breakpoints.up("lg")]: {
					maxWidth: "70%"
				}
			},
			"& .mco-animation-01__text--2": {
				...theme?.typography?.h5,
				color: theme.palette.text.secondary,
				opacity: 0,
				marginTop: theme.spacing(1),
				textShadow: "0px 4px 20px rgba(0, 0, 0, 0.5)",
				transform: "translate(0, 5px)",
				transition: "opacity 2500ms ease 1s, transform 2000ms ease 1s",
				[theme.breakpoints.up("lg")]: {
					marginTop: theme.spacing(2),
					maxWidth: "70%"
				}
			},
			"&:before": {
				backgroundImage:
					"url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOSIgaGVpZ2h0PSI4NSIgdmlld0JveD0iMCAwIDkgODUiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik00LjAyMTQ4IDBMNC4wMjE0OSA4MCIgc3Ryb2tlPSJ1cmwoI3BhaW50MF9saW5lYXJfMTA0XzU1OTApIiBzdHJva2Utd2lkdGg9IjIiLz4KPHBhdGggZD0iTTAuNDg3ODk4IDgxLjE5OTVDLTAuMTYyNjMzIDgwLjU0ODkgLTAuMTYyNjMzIDc5LjQ5NDIgMC40ODc4OTggNzguODQzN0wyLjg0MzY4IDc2LjQ4NzlDMy40OTQyMSA3NS44Mzc0IDQuNTQ4OTMgNzUuODM3NCA1LjE5OTQ2IDc2LjQ4NzlMNy41NTUyNCA3OC44NDM3QzguMjA1NzcgNzkuNDk0MiA4LjIwNTc3IDgwLjU0ODkgNy41NTUyNCA4MS4xOTk1TDUuMTk5NDYgODMuNTU1MkM0LjU0ODkzIDg0LjIwNTggMy40OTQyMSA4NC4yMDU4IDIuODQzNjggODMuNTU1MkwwLjQ4Nzg5OCA4MS4xOTk1WiIgZmlsbD0iI0Y1RjFFQiIvPgo8ZGVmcz4KPGxpbmVhckdyYWRpZW50IGlkPSJwYWludDBfbGluZWFyXzEwNF81NTkwIiB4MT0iMy41MjE2NiIgeTE9IjMuNzQ1MzRlLTA4IiB4Mj0iMy41MjE2NyIgeTI9IjgwIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIHN0b3AtY29sb3I9IndoaXRlIiBzdG9wLW9wYWNpdHk9IjAiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSJ3aGl0ZSIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo=')",
				content: "''",
				height: 84,
				opacity: 0,
				marginBottom: theme.spacing(3),
				position: "relative",
				transition: "opacity 1500ms ease",
				width: 8,
				zIndex: 2,
				[theme.breakpoints.up("lg")]: {
					marginBottom: theme.spacing(8)
				}
			}
		},
		"&:before": {
			background: "linear-gradient(360deg, rgba(32, 48, 61, 0) 54.66%, #20303D 98.41%)",
			content: "''",
			height: "90%",
			left: 0,
			position: "absolute",
			pointerEvents: "none",
			top: 0,
			width: "100%",
			zIndex: 2
		},
		"&:after": {
			background: "linear-gradient(180deg, rgba(32, 48, 61, 0) 54.66%, #20303D 98.41%)",
			content: "''",
			height: "70%",
			left: 0,
			position: "absolute",
			pointerEvents: "none",
			bottom: 0,
			width: "100%",
			zIndex: 2
		}
	},
	active: {
		"& .mco-animation-01__text--1": {
			transform: "translate(0, 0) !important",
			opacity: "1 !important"
		},
		"& .mco-animation-01__text--2": {
			transform: "translate(0, 0) !important",
			opacity: "1 !important"
		},
		"& .mco-animation-01__texts:before": {
			opacity: "1 !important"
		}
	},
	animation: {
		height: "100%",
		position: "relative",
		width: "100%",
		zIndex: 1
	}
}));

const Animation = ({ animationID, data, lang, layoutVariant }) => {
	const { REACT_APP_PATH } = process.env;
	const [active, setActive] = useState(false);
	const css = useStyles();

	useEffect(() => {
		setTimeout(() => {
			setActive(true);
		}, 1500);

		return () => clearTimeout(active);
	}, [active]);

	const options = {
		mediaFormats: function () {
			if (layoutVariant === "fullscreen")
				return { xs: "animation-theme-mobile", sm: "animation-theme" };
			return { xs: "animation-mobile", sm: "animation" };
		}
	};

	return (
		<div
			className={clsx(css.root, {
				[css.rootFullscreen]: layoutVariant === "fullscreen",
				[css.active]: active === true
			})}
		>
			<div className={clsx("mco-animation", css.animation)}>
				{data?.items ? (
					<Suspense fallback={<></>}>
						<ReactAnimation01
							absoluteMediaURLs
							data={data}
							lang={lang}
							key="data"
							mediaFormats={options.mediaFormats()}
							webpFallback={true}
						/>
					</Suspense>
				) : (
					<>
						{animationID && (
							<Suspense fallback={<></>}>
								<ReactAnimation01
									api={`${REACT_APP_PATH}/animationV3.json.api`}
									lang={lang}
									id={animationID}
									key="api"
									mediaFormats={options.mediaFormats()}
									webpFallback={true}
								/>
							</Suspense>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default Animation;
